import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, SanctionDTO } from '@/services/api.service';
import { useAuthStore } from '@/stores/auth.store';
import { SanctionModal } from '@/components/SanctionModal';
import { formatPlayerName } from '@/utils/playerName';
import styles from './SanctionsPage.module.scss';

type SanctionType = 0 | 1 | 2 | null;
type PageSize = 10 | 25 | 50 | 100;

export function SanctionsPage() {
  const navigate = useNavigate();
  const hasPage = useAuthStore((s) => s.hasPage);
  const canSanction = hasPage('sanctions.create');
  const [showModal, setShowModal] = useState(false);
  const [sanctions, setSanctions] = useState<SanctionDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSize>(25);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [typeFilter, setTypeFilter] = useState<SanctionType>(null);

  const fetchSanctions = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await apiService.getSanctions({
      page,
      pageSize,
      search: search || undefined,
      type: typeFilter ?? undefined,
    });

    if (result.error) {
      setError(result.error);
      setSanctions([]);
    } else if (result.data) {
      setSanctions(result.data.items);
      setTotalPages(result.data.totalPages);
      setTotal(result.data.total);
    }

    setLoading(false);
  }, [page, pageSize, search, typeFilter]);

  useEffect(() => {
    fetchSanctions();
  }, [fetchSanctions]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(0);
  };

  const handlePageSizeChange = (newSize: PageSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const handleTypeFilter = (type: SanctionType) => {
    setTypeFilter(type);
    setPage(0);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTypeClass = (type: number) => {
    switch (type) {
      case 0: return styles.typeBan;
      case 1: return styles.typeMute;
      case 2: return styles.typeWarn;
      default: return '';
    }
  };

  const handlePlayerClick = (uuid: string | null, e: React.MouseEvent) => {
    e.stopPropagation();
    if (uuid) {
      navigate(`/players/${uuid}`);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerTop}>
          <div>
            <h1 className={styles.title}>Sanctions</h1>
            <p className={styles.subtitle}>
              Gerez les bans, mutes et warns des joueurs
            </p>
          </div>
          {canSanction && (
            <button className={styles.sanctionBtn} onClick={() => setShowModal(true)}>
              + Sanctionner
            </button>
          )}
        </div>

        <div className={styles.controls}>
          <form className={styles.searchForm} onSubmit={handleSearch}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Rechercher un joueur (pseudo Recube ou gamertag), staff ou raison..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className={styles.searchButton}>
              Rechercher
            </button>
          </form>

          <div className={styles.filters}>
            <div className={styles.typeFilters}>
              <button
                className={`${styles.filterButton} ${typeFilter === null ? styles.active : ''}`}
                onClick={() => handleTypeFilter(null)}
              >
                Tous
              </button>
              <button
                className={`${styles.filterButton} ${styles.filterBan} ${typeFilter === 0 ? styles.active : ''}`}
                onClick={() => handleTypeFilter(0)}
              >
                Bans
              </button>
              <button
                className={`${styles.filterButton} ${styles.filterMute} ${typeFilter === 1 ? styles.active : ''}`}
                onClick={() => handleTypeFilter(1)}
              >
                Mutes
              </button>
              <button
                className={`${styles.filterButton} ${styles.filterWarn} ${typeFilter === 2 ? styles.active : ''}`}
                onClick={() => handleTypeFilter(2)}
              >
                Warns
              </button>
            </div>

            <select
              className={styles.pageSizeSelect}
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value) as PageSize)}
            >
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </div>
        </div>
      </header>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
          </div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : sanctions.length === 0 ? (
          <div className={styles.empty}>Aucune sanction trouvee</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Type</th>
                <th>Joueur</th>
                <th>Staff</th>
                <th>Raison</th>
                <th>Duree</th>
                <th>Date</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {sanctions.map((sanction) => (
                <tr key={sanction.id} className={sanction.revoked ? styles.revoked : ''}>
                  <td>
                    <span className={`${styles.typeBadge} ${getTypeClass(sanction.type)}`}>
                      {sanction.typeName}
                    </span>
                  </td>
                  <td className={styles.playerCell}>
                    {sanction.victimUuid ? (
                      <button
                        className={styles.playerLink}
                        onClick={(e) => handlePlayerClick(sanction.victimUuid, e)}
                      >
                        {formatPlayerName(sanction.victimName, sanction.victimDisplayName)}
                      </button>
                    ) : (
                      formatPlayerName(sanction.victimName, sanction.victimDisplayName)
                    )}
                  </td>
                  <td className={styles.staffCell}>
                    {formatPlayerName(sanction.staffName, sanction.staffDisplayName, 'Systeme')}
                  </td>
                  <td className={styles.reasonCell}>
                    <span className={styles.reason} title={sanction.description || ''}>
                      {sanction.description || '-'}
                    </span>
                  </td>
                  <td>{sanction.duration || '-'}</td>
                  <td>{formatDate(sanction.createAt)}</td>
                  <td>
                    {sanction.revoked ? (
                      <span className={styles.statusRevoked}>Revoquee</span>
                    ) : sanction.temporary && sanction.expiryAt && sanction.expiryAt < Date.now() ? (
                      <span className={styles.statusExpired}>Expiree</span>
                    ) : (
                      <span className={styles.statusActive}>Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!loading && !error && totalPages > 0 && (
        <div className={styles.pagination}>
          <div className={styles.paginationInfo}>
            Affichage {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} sur {total}
          </div>

          <div className={styles.paginationControls}>
            <button
              className={styles.pageButton}
              onClick={() => setPage(0)}
              disabled={page === 0}
            >
              &laquo;
            </button>
            <button
              className={styles.pageButton}
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
            >
              &lsaquo;
            </button>

            <span className={styles.pageInfo}>
              Page {page + 1} / {totalPages}
            </span>

            <button
              className={styles.pageButton}
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages - 1}
            >
              &rsaquo;
            </button>
            <button
              className={styles.pageButton}
              onClick={() => setPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
            >
              &raquo;
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <SanctionModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            // Reviens en page 0 pour voir la nouvelle sanction (en tête).
            // Si déjà en page 0, setPage est un no-op -> refetch explicite ;
            // sinon on laisse le changement de page déclencher un seul fetch (page 0).
            if (page === 0) void fetchSanctions();
            else setPage(0);
          }}
        />
      )}
    </div>
  );
}
